import { DEFAULT_SELECTED_STATS, GAMES_STAT_ABBR } from '../constants'
import type { ProgressPeriod } from '../types'
import { listChartStats, periodValue } from './progressChart'
import { isDerivedStat } from './statValues'

/** Periods in which the stat has a value other than 0. */
const countActivePeriods = (series: ProgressPeriod[], abbr: string): number =>
  series.filter((period) => (periodValue(period, abbr) ?? 0) !== 0).length

/**
 * The stats charted before the user picks any: the first chip (Goals for a
 * skater) plus the count stats with the most active periods in the range, in
 * chip order. Rates are skipped (PTS/G repeats Points' shape), and so is a stat
 * that never moves — an empty line is no use as a default.
 */
export const pickDefaultStats = (
  series: ProgressPeriod[],
  chartStats: string[]
): string[] => {
  const [first, ...rest] = chartStats
  if (first === undefined) {
    return []
  }
  const picked = rest
    .filter((abbr) => !isDerivedStat(abbr))
    .map((abbr, order) => ({
      abbr,
      order,
      active: countActivePeriods(series, abbr),
    }))
    .filter(({ active }) => active > 0)
    .sort((a, b) => b.active - a.active || a.order - b.order)
    .slice(0, DEFAULT_SELECTED_STATS - 1)
    .sort((a, b) => a.order - b.order)
    .map(({ abbr }) => abbr)
  return [first, ...picked]
}

/**
 * The stats on the chart before the user picks any: the defaults above, plus
 * Games Played, which stays until the user turns its chip off.
 */
export const pickProgressDefaults = (series: ProgressPeriod[]): string[] => {
  const stats = pickDefaultStats(series, listChartStats(series))
  return stats.length > 0 ? [...stats, GAMES_STAT_ABBR] : stats
}

/** The defaults picked for one player + sport + role, kept across ranges. */
export interface FrozenDefaults {
  key: string
  stats: string[]
}

export interface ResolveFrozenDefaultsArgs {
  frozen: FrozenDefaults | null
  /** Player + sport + role: a new key picks again. */
  key: string
  /** What `pickDefaultStats` gives for the data on screen. */
  picked: string[]
  /** The data is this key's own (loaded, not a placeholder). */
  isReady: boolean
}

/**
 * The defaults are picked once per player + sport + role, from the first range
 * that loads, so changing the range never reshuffles them. Returns the stats to
 * show and the frozen value to keep (`null` to keep the current one).
 */
export const resolveFrozenDefaults = ({
  frozen,
  key,
  picked,
  isReady,
}: ResolveFrozenDefaultsArgs): {
  stats: string[]
  next: FrozenDefaults | null
} => {
  if (frozen?.key === key) {
    return { stats: frozen.stats, next: null }
  }
  if (!isReady) {
    return { stats: picked, next: null }
  }
  return { stats: picked, next: { key, stats: picked } }
}
